import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,    // 2 min — data stays fresh without refetch
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (err) => {
        console.error('Mutation error:', err);
      },
    },
  },
});

/** The Crew Schedule's Temps active box (#584): `GET /manpower/temps-active`. */
export const TEMPS_ACTIVE_QUERY_KEY = ['manpower-temps-active'] as const;

/** The Crew Schedule's Subcontractors box (#688): `GET /manpower/subcontractors-active`. */
export const SUBCONTRACTORS_ACTIVE_QUERY_KEY = ['manpower-subcontractors-active'] as const;

/**
 * Every cache of the config itself, plus the manpower reads built from it,
 * invalidated from one place after any `PUT /manpower/config/:key`.
 *
 * ⚠️ THE SAME ENDPOINT IS CACHED UNDER TWO KEYS. The Crew Schedule and the Gap
 * Analysis read `GET /manpower/config` as `['manpower-config']`, while Admin →
 * Config and the project tabs read it as `['manpower', 'config']`. Each writer
 * used to invalidate only its own. With the two-minute `staleTime` and no focus
 * refetch above, a value saved on one screen showed its old figure (or no row
 * at all) on the other, ready to be edited and saved back over the new one
 * (Codex, #584). Available MP, the Gap Analysis boxes and the typed boxes
 * (Temps active, Subcontractors) are each editable from more than one screen.
 *
 * Also the reads that build their figures from `mp_config` on the server: the
 * typed boxes, whose count and stamp are that row, and the Gap Analysis, whose
 * supply lines are its own boxes and Available MP.
 *
 * Four writers: Available MP on the Crew Schedule, the typed boxes
 * (TypedHeadcountBox), the Gap Analysis boxes, and Admin → Config.
 *
 * ⚠️ AN UNMOUNTED SCREEN'S COPY IS REMOVED, NOT JUST INVALIDATED. Invalidating
 * an inactive query only marks it stale: it keeps the old data and doesn't
 * refetch. When that screen mounts again, React Query renders the old value at
 * once and refetches in the background, and Admin → Config and the typed boxes
 * all enable editing as soon as there is data. An edit in that
 * window starts from the old figure and can save over the one just entered
 * elsewhere (Codex, #584). Removed instead, the next visit starts with no data,
 * so it shows its loading state and offers no edit until the stored value has
 * arrived. Queries a screen is showing right now are refetched in place.
 *
 * ⚠️ NOT every server read of `mp_config`. The labor rate and the cost
 * visibility switch, both edited in Admin → Config, also feed figures the API
 * computes: the Man Hours report, projected hours, materials and the leadership
 * page. Those caches are not cleared here and keep their own freshness windows,
 * as they did before this helper existed. Covering them means adding their
 * keys here, deliberately, rather than assuming this list already does.
 */
export function invalidateManpowerConfigQueries(qc: QueryClient): Promise<void> {
  const keys: readonly (readonly unknown[])[] = [
    ['manpower-config'],
    ['manpower', 'config'],
    TEMPS_ACTIVE_QUERY_KEY,
    SUBCONTRACTORS_ACTIVE_QUERY_KEY,
    ['manpower', 'gap-analysis'],
  ];
  for (const queryKey of keys) qc.removeQueries({ queryKey, type: 'inactive' });
  return Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey }))).then(() => undefined);
}

/**
 * Everything that goes stale when a job's address changes.
 *
 * ⚠️ `['projects']` IS LOAD-BEARING, not belt-and-braces. It is a PREFIX, and two
 * caches live under it that a bare `['project', id]` misses: the Projects list
 * (`['projects', params]`) and the project activity feed
 * (`['projects', 'activity', id]`). Setting an address writes a `project-edited`
 * event, so without this the Overview page keeps showing an activity feed that
 * does not mention the change the user just made, and the list can serve — or
 * export — the old address for the length of its freshness window. (Codex, #479.)
 *
 * ⚠️ `['address-review']` IS THE WORKLIST, and it belongs here for the same
 * reason. Setting an address on the PROJECT page resolves a worklist row, and
 * clearing one creates a new row; with a two-minute global `staleTime` and no
 * override on that query, Admin → Job Addresses would otherwise show work that is
 * already done — or claim to be empty when it is not. Acting on such a row is
 * refused by the staleness guard rather than corrupting anything, so this is
 * about not showing somebody an error they had no way to avoid. (Codex, #479.)
 *
 * ⚠️ Deliberately NOT `['kpi']` or `['project-counts']`, which the generic Job Info
 * save also invalidates: neither depends on the address, and adding them here
 * would be copying the longer list rather than understanding it.
 *
 * Three call sites — the picker's pick and remove, and the admin worklist — so it
 * lives here instead of being copied into each and drifting.
 */
export function invalidateAddressQueries(
  qc: QueryClient,
  projectId: number,
): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['project', projectId] }),
    qc.invalidateQueries({ queryKey: ['projects'] }),
    qc.invalidateQueries({ queryKey: ['address-review'] }),
  ]).then(() => undefined);
}

/**
 * Everything that reads `projects.travel_job`, invalidated from one place.
 *
 * ⚠️ THIS EXISTS BECAUSE THE HAND-MAINTAINED VERSION KEPT BEING WRONG. Two
 * surfaces write this column — the Travel/Local toggle on the crew schedule and
 * the Job Info card on the project Overview — and each grew its own list of
 * caches to clear. Review found a missing one three separate times, in both
 * directions: the toggle not refreshing the project caches, the Overview save
 * not refreshing the manpower caches, and neither refreshing the address
 * worklist. Every one of those was a one-line omission that showed a user a
 * screen quietly disagreeing with the database, and every fix left the OTHER
 * writer still wrong. A shared list is the only version of this that converges.
 *
 * The consumers, and why each is here:
 *
 * - `project` / `projects` — the column rides on PROJECT_COLS, so the workspace
 *   and the portfolio list both hold a copy.
 * - `manpower-schedule` — the grid's Type column.
 * - `manpower-project-options` — the rows the grid synthesizes for jobs with no
 *   crew in the window; they carry travel_job too.
 * - `manpower, schedule-summary` — the project Manpower tab's leader row, which
 *   labels itself "Traveling leaders needed" or "Leaders needed" off this.
 * - `manpower, gap-analysis` — the whole travel/local split.
 * - `address-review` — the least obvious, and the one most often missed: Admin →
 *   Job Addresses lists a job whose address is unverified AND (has address text
 *   OR travel_job = 1). Flipping this column therefore ADDS a blank-address job
 *   to that worklist, or removes it. Two-minute staleTime, no refetch on focus.
 *
 * ⚠️ Deliberately NOT `kpi` or `project-counts`: neither depends on travel_job,
 * and adding them would be copying a longer list rather than understanding it —
 * the same reasoning `invalidateAddressQueries` records above.
 */
export function invalidateTravelJobQueries(
  qc: QueryClient,
  projectId: number,
): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['project', projectId] }),
    qc.invalidateQueries({ queryKey: ['projects'] }),
    qc.invalidateQueries({ queryKey: ['manpower-schedule'] }),
    qc.invalidateQueries({ queryKey: ['manpower-project-options'] }),
    qc.invalidateQueries({ queryKey: ['manpower', 'schedule-summary', projectId] }),
    qc.invalidateQueries({ queryKey: ['manpower', 'gap-analysis'] }),
    qc.invalidateQueries({ queryKey: ['address-review'] }),
  ]).then(() => undefined);
}

/**
 * Everything that goes stale when the set of projects YOU are on changes, or
 * when one of them stops being live.
 *
 * ⚠️ THE ONE THAT IS EASY TO MISS IS `['me']`. It is not user identity here —
 * it also carries `hasActiveAssignments`, which decides whether the Today tab
 * is in the nav at all and whether `/today` renders or redirects. So a save
 * that makes you the PM of a job, or completes/archives/deletes your last one,
 * changes a value that the app shell has already cached and will not refetch on
 * its own: `refetchOnWindowFocus` is false globally, and `AuthenticatedShell`
 * mounts once per session. Put yourself on your first job and the Today tab
 * does not appear; finish your last one and the tab stays, opening the empty
 * page the gate exists to prevent. (Codex, #510.)
 *
 * It looked self-healing in testing only by accident: half a dozen unrelated
 * components observe `['me']` too, so visiting Equipment, PPR, Manpower or
 * Admin refetches it. That is not a mechanism, it is a coincidence — and one
 * that disappears the day somebody refactors those pages.
 *
 * - `me` — `hasActiveAssignments`, as above.
 * - `today` — a PREFIX, because the key is `['today', userId, role]` and the
 *   payload holds the active count, the mobilization strip and the action
 *   items, every one of which is scoped to exactly this set of projects.
 * - `project` / `projects` — the team, status and archive flag all ride on the
 *   project record, and `projects` is a prefix covering the list and the
 *   activity feed, as `invalidateAddressQueries` records.
 *
 * ⚠️ Deliberately NOT the manpower or address caches. Neither reads the
 * assignment slots, and adding them would be copying a longer list rather than
 * understanding it — the same reasoning the two helpers above record.
 *
 * ⚠️ `projectId` is optional because one caller has no single project: the CSV
 * import writes many at once. Omitting it still clears the `projects` prefix,
 * which is what that case needs.
 */
export function invalidateAssignmentQueries(
  qc: QueryClient,
  projectId?: number,
): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['me'] }),
    qc.invalidateQueries({ queryKey: ['today'] }),
    qc.invalidateQueries({ queryKey: ['projects'] }),
    ...(projectId == null ? [] : [qc.invalidateQueries({ queryKey: ['project', projectId] })]),
  ]).then(() => undefined);
}

/**
 * Everything that goes stale when a job is linked to the bid it duplicates
 * (POST /projects/:id/link-bid).
 *
 * It writes TWO projects. The job takes the bid, and for a won one the won stamp, the
 * proof, the status and the bid's address; the placeholder is ARCHIVED. Beyond the
 * assignment caches (the placeholder may have been somebody's job, and the job's own
 * record and the list changed):
 *
 * - `['project', placeholderId]` — the archived placeholder's own detail (see below).
 * - `bid-placeholders` — the archived placeholder must stop being offered.
 * - `manpower-project-options` — the grid builds its forecast rows from the non-archived
 *   projects, so the placeholder's bid forecast leaves it. The grid keeps that query
 *   mounted, so its `staleTime` may never rescue it (see `invalidateStartAlignmentQueries`).
 * - `manpower, gap-analysis` — its pursuit series reads the same archived-or-not rule.
 * - `address-review` — the job may just have been given the bid's address, which is
 *   what puts an unverified one on the admin worklist.
 * - `kpi` — the status can move Pipeline -> Preconstruction, and the placeholder leaves
 *   the counts.
 *
 * ⚠️ Deliberately NOT `manpower-schedule`: it holds booked crew, and the link is refused
 * while any is booked on the placeholder, so nothing on it moves.
 */
export function invalidateBidLinkQueries(
  qc: QueryClient,
  projectId: number,
  placeholderId: number,
): Promise<void> {
  return Promise.all([
    invalidateAssignmentQueries(qc, projectId),
    // The placeholder's own detail: it is archived and unlinked now, and a workspace
    // viewed inside the stale window would otherwise come back showing it live and linked.
    qc.invalidateQueries({ queryKey: ['project', placeholderId] }),
    qc.invalidateQueries({ queryKey: ['bid-placeholders'] }),
    qc.invalidateQueries({ queryKey: ['manpower-project-options'] }),
    qc.invalidateQueries({ queryKey: ['manpower', 'gap-analysis'] }),
    qc.invalidateQueries({ queryKey: ['address-review'] }),
    qc.invalidateQueries({ queryKey: ['kpi'] }),
  ]).then(() => undefined);
}

/**
 * Everything that goes stale when a project's DERIVED HEALTH could have moved.
 *
 * ⚠️ HEALTH IS NEVER STORED. `deriveHealth` computes it on read, so nothing
 * announces that a verdict changed — the only signal is that one of its inputs
 * was written. Those inputs are:
 *
 *   - `projects.original_start_date` / `current_start_date` / `estimated_completion`
 *   - `projects.project_status_id` (start-slip only fires in Construction, #399)
 *   - `projects.mobilization_status_id`
 *   - `MAX(mobilizations.end_date)`, through `effectiveCompletion` — a sanctioned
 *     remobilization past the target date means the job is NOT late
 *   - **whether the assigned PM is still a live user.** Both leadership queries
 *     join `users ... AND deleted_at IS NULL`, so removing somebody moves every
 *     job they hold onto "No project manager assigned" and blanks their name on
 *     the health rows; restoring them moves it all back. Nothing about the
 *     PROJECT changes — the edit is on the person. (Codex, P2 on #513.)
 *
 * ⚠️ AND THE WRITERS ARE NOT THE OBVIOUS ONES. `invalidateAssignmentQueries`
 * already covers the writers that touch the assignment slots, and those happen
 * to cover most date edits too. What it does not cover is the three that change
 * a health input WITHOUT touching a person:
 *
 *   - deleting a mobilization — drops it out of the `effectiveCompletion` max,
 *     so a job excused by a remob becomes late again
 *   - saving phase dates — `upsertPhases` calls `syncLegacyScheduleDates`, which
 *     runs `UPDATE projects SET current_start_date, original_start_date,
 *     estimated_completion`, the three columns the rule reads
 *   - accepting the mobilization-end divergence, which is `updatePhases` again
 *   - removing or restoring a USER in Admin, which touches no project at all
 *
 * All three invalidated `project`, `projects` and `kpi` and left `['today']`
 * alone, so both Today pages served the obsolete verdict until their own poll.
 * On the leadership view that is not a detail at the edge of a screen — health
 * exceptions ARE the page. (Codex, P2 on #513.)
 *
 * ⚠️ Deliberately NOT `['me']`. None of these writers can change who is on a
 * job, and copying the longer list rather than understanding it is the mistake
 * the helper above records.
 */
export function invalidateHealthQueries(
  qc: QueryClient,
  projectId?: number,
): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['today'] }),
    qc.invalidateQueries({ queryKey: ['projects'] }),
    ...(projectId == null ? [] : [qc.invalidateQueries({ queryKey: ['project', projectId] })]),
  ]).then(() => undefined);
}

/**
 * Everything that goes stale when a job's READINESS or its BOOKED CREW changes.
 *
 * ⚠️ THESE TWO SIGNALS NOW DRIVE A LEADERSHIP SECTION EACH, and neither writer
 * used to clear `['today']`. Slice 2 added "Mobilizing with readiness gaps" and
 * "No crew booked", both derived on read exactly as health is — nothing stores
 * them, so nothing announces that one moved. The only signal is that an input
 * was written:
 *
 *   - `project_checklist_values` — the Mobilization tab's checklist save. It
 *     invalidated `project`, `projects`, `project-counts` and `kpi` and left
 *     `['today']` alone, so qualifying a job's last critical criterion left the
 *     readiness row on the leadership page until its own five-minute poll.
 *   - `mp_schedule_entries` — PUBLISHING a crew draft, and clearing a job's
 *     future entries. The draft layer does not count: `crew_entries` reads
 *     published rows, so staging a cell changes nothing this page can see.
 *
 * ⚠️ AND THIS WAS ALREADY WRONG FOR THE PM PAGE, not just the new one. Its
 * mobilization strip scores the same checklist and its action items read the
 * same `crew_count > 0`, through the SAME shared derivations
 * (`readChecklistTallies`). So both pages were serving an obsolete answer from
 * the same omission, and the leadership view only made it visible.
 *
 * ⚠️ Deliberately NOT `['me']`: neither writer can change who is on a job.
 * Deliberately NOT the address or travel caches, which read neither. Copying a
 * longer list rather than understanding it is the mistake the helpers above
 * record.
 *
 * `projectId` is optional because the crew-schedule writers are company-wide —
 * a publish can span many jobs, and clearing the `projects` prefix is what that
 * case needs.
 */
export function invalidateReadinessQueries(
  qc: QueryClient,
  projectId?: number,
): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['today'] }),
    qc.invalidateQueries({ queryKey: ['projects'] }),
    ...(projectId == null ? [] : [qc.invalidateQueries({ queryKey: ['project', projectId] })]),
  ]).then(() => undefined);
}

/**
 * Caches feeding the START-MISMATCH flag (#385), after either of its two inputs
 * moves.
 *
 * The same shape as the readiness omission above, one derivation along: nothing
 * stores either input, so nothing announces that one moved. The flag compares a
 * job's PLANNED START against its FIRST BOOKED CREW DAY, and **both sides have a
 * writer** — which is what makes this easy to half-wire:
 *
 *   - the crew day is MIN(work_date) over published `mp_schedule_entries`, moved
 *     by publishing drafts, clearing a job's future entries (TWO call sites), and
 *     the Excel import;
 *   - the planned start is `projects.current_start_date`, moved by the phase save
 *     on the Schedule & Dates drawer.
 *
 * Two caches carry them:
 *
 *   - `['manpower-project-options', …]` — the grid merges BOTH onto every row, so
 *     it goes stale from either writer. It stays MOUNTED for the whole session,
 *     so a 60-second `staleTime` never refetches it on its own: publish a job's
 *     first crew day, or re-plan its start, and the badge does not move until a
 *     reload.
 *   - `['manpower', 'schedule-summary', projectId]` — the per-job crew summary
 *     the project Manpower tab and the Overview card both read.
 *
 * ⚠️ EVERY WRITER OF EITHER INPUT CALLS THIS, and there are five. `SyncModal` is
 * the one that gets forgotten: `invalidateAssignmentQueries` has a note about the
 * same trap, and `SyncModal`'s own `clearFuture` carries a comment calling itself
 * "THE SECOND clearFutureEntries CALLER" — and it was still missed here on the
 * first pass. If you add a sixth writer, add it here too.
 *
 * ⚠️ SEPARATE from `invalidateReadinessQueries`, not folded into it. That one is
 * also called by the Mobilization checklist save, which moves neither input — and
 * its own header warns that copying a longer list rather than understanding it is
 * the mistake these helpers exist to record.
 *
 * ⚠️ Staging a draft is deliberately NOT a caller. The crew day is derived from
 * published rows only — the same anchoring the "Needs Scheduling" grouping uses —
 * so a badge that moved while a scheduler was still staging would report a
 * disagreement nobody had committed to yet.
 *
 * A plan-only edit clears the crew summary too, which is one redundant refetch of
 * a small per-job endpoint. Paid deliberately: one helper both writers call is
 * harder to half-wire than two that look nearly identical, and this file's whole
 * history is the half-wired case.
 *
 * `projectId` is optional because a publish or an import spans many jobs; the
 * per-job summary is cleared by prefix when it is absent.
 */
export function invalidateStartAlignmentQueries(
  qc: QueryClient,
  projectId?: number,
): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['manpower-project-options'] }),
    qc.invalidateQueries({
      queryKey: projectId == null
        ? ['manpower', 'schedule-summary']
        : ['manpower', 'schedule-summary', projectId],
    }),
  ]).then(() => undefined);
}
